import React, { useState } from "react";
import {
	Box,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemText,
	Switch,
	TextField,
	Tooltip,
} from "@mui/material";
import { ScraperConfigOptionValue } from "./types";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import ClearIcon from "@mui/icons-material/Clear";
import { PromiseResult } from "../../context/AdminPanelContext/types";
import { confirmAlert } from "react-confirm-alert";

interface ConfigValueItemProps {
	configValue: ScraperConfigOptionValue;
	handleDelete: (configValueId: number) => void;
	handleSave: (
		configValue: ScraperConfigOptionValue,
		updateActive?: boolean
	) => Promise<PromiseResult>;
}

enum Action {
	SAVE = "Save",
	DELETE = "Delete",
}

const ConfigValueItem: React.FC<ConfigValueItemProps> = ({
	configValue,
	handleDelete,
	handleSave,
}) => {
	const [value, setValue] = useState(configValue.value || "");
	const [isActive, setIsActive] = useState(configValue.active);
	const [hasError, setHasError] = useState(false);
	const [isEdited, setIsEdited] = useState(false);
	const labelId = `list-item-label-${configValue.value}`;

	const handleValueChange = (e) => {
		setHasError(false);
		setValue(e.target.value);
	};

	const handleUndo = () => {
		setIsEdited(false);
		setValue(configValue.value);
		setIsActive(configValue.active);
	};

	const handleUpdate = async () => {
		const updatedLabelValue = {
			id: configValue.id,
			value: value,
			active: isActive,
		};
		if (configValue.id > 0 && configValue.value === value) {
			if (configValue.active === isActive) return setIsEdited(false);
			return await handleSave(updatedLabelValue, true);
		}
		const result = await handleSave(updatedLabelValue);
		result.success ? setIsEdited(false) : setHasError(true);
	};

	const submit = (action: Action) => {
		const isDelete = action === Action.DELETE;
		confirmAlert({
			title: `${action} scraper config option value`,
			message: isDelete
				? `Are you sure you want to delete scraper config option value with Id: ${configValue.id}; value: ${configValue.value}?`
				: `Are you sure you want to save scraper config option value with Id: ${configValue.id}`,
			buttons: [
				{
					label: "Confirm",
					onClick: isDelete
						? () => handleDelete(configValue.id)
						: () => handleUpdate(),
				},
				{
					label: "Cancel",
				},
			],
		});
	};

	return (
		<Box
			sx={{
				height: "60px",
				backgroundColor: "white",
				width: "100%",
				borderRadius: "5px",
				display: "flex",
			}}
		>
			{configValue.id < 0 ? (
				<ListItem>
					<Tooltip title={value}>
						<TextField
							onChange={handleValueChange}
							value={value}
							size="small"
						/>
					</Tooltip>
					<IconButton
						color="success"
						disabled={!value || hasError}
						onClick={() => submit(Action.SAVE)}
					>
						<CheckIcon />
					</IconButton>
					<IconButton color="error" onClick={() => submit(Action.DELETE)}>
						<ClearIcon />
					</IconButton>
				</ListItem>
			) : (
				<>
					{isEdited ? (
						<ListItem>
							<Tooltip title={value}>
								<TextField
									onChange={handleValueChange}
									value={value}
									size="small"
								/>
							</Tooltip>
							<Switch
								color="success"
								size="small"
								checked={isActive}
								onChange={(e) => setIsActive(e.target.checked)}
							/>
							<IconButton
								color="success"
								disabled={!value || hasError}
								onClick={() => submit(Action.SAVE)}
							>
								<CheckIcon />
							</IconButton>
							<IconButton color="error" onClick={() => submit(Action.DELETE)}>
								<ClearIcon />
							</IconButton>
							<IconButton color="primary" onClick={() => handleUndo()}>
								<UndoIcon />
							</IconButton>
						</ListItem>
					) : (
						<ListItemButton>
							<Tooltip title={`Id: ${configValue.id}`}>
								<ListItemText
									id={labelId}
									onClick={() => setIsEdited(true)}
									primary={configValue.value || '" "'}
								/>
							</Tooltip>
						</ListItemButton>
					)}
				</>
			)}
		</Box>
	);
};

export default ConfigValueItem;
